import { create } from 'zustand';
import { adminCategoryManagementApi } from '@api/api-config';
import { AdminCategoryTreeNodeResponse } from '@api/generated/portal-be-admin';

interface CategoryTreeState {
  tree: AdminCategoryTreeNodeResponse[];
  isLoading: boolean;
  error: string | null;
  expandedIds: Set<number>;
  selectedId: number | null;
}

interface CategoryTreeActions {
  fetchCategoryTree: (query?: string) => Promise<void>;
  toggleExpand: (id: number) => void;
  expandAll: () => void;
  collapseAll: () => void;
  selectCategory: (id: number | null) => void;
}

function collectAllIds(nodes: AdminCategoryTreeNodeResponse[]): Set<number> {
  const ids = new Set<number>();
  const stack = [...nodes];
  while (stack.length > 0) {
    const node = stack.pop()!;
    if (node.children && node.children.length > 0) {
      ids.add(node.id);
      stack.push(...node.children);
    }
  }
  return ids;
}

export const useCategoryTreeStore = create<CategoryTreeState & CategoryTreeActions>((set, get) => ({
  tree: [],
  isLoading: false,
  error: null,
  expandedIds: new Set<number>(),
  selectedId: null,

  fetchCategoryTree: async (query?: string) => {
    set({ isLoading: true, error: null });

    try {
      const response = await adminCategoryManagementApi.getCategoryTree({
        query: query,
      });

      const tree = response.data || [];
      const expandedIds = query ? collectAllIds(tree) : new Set<number>();

      set({
        tree,
        isLoading: false,
        error: null,
        expandedIds,
      });
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || 'Nepodařilo se načíst strom kategorií';
      set({
        error: errorMessage,
        isLoading: false,
      });
    }
  },

  toggleExpand: (id: number) => {
    set(state => {
      const next = new Set(state.expandedIds);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return { expandedIds: next };
    });
  },

  expandAll: () => {
    const allIds = collectAllIds(get().tree);
    set({ expandedIds: allIds });
  },

  collapseAll: () => {
    set({ expandedIds: new Set<number>() });
  },

  selectCategory: (id: number | null) => {
    set({ selectedId: id });
  },
}));
